import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import usePageTitle from "@/hooks/usePageTitle";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

export default function ForgotPasswordPage() {
  usePageTitle("Mot de passe oublie");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) { setError("Veuillez saisir votre adresse email."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.detail || "Erreur"); return; }
      setSent(true);
    } catch {
      setError("Erreur serveur. Veuillez reessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#080C18] flex items-center justify-center px-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo-glotech-icon.png" alt="GLOTECH" className="h-14 w-14 mx-auto rounded-xl object-cover" />
        </div>

        <div className="bg-[#0F1629] border border-slate-700/50 rounded-2xl p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-password-success">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Email envoye</h2>
              <p className="text-slate-400 text-sm leading-relaxed">
                Si un compte est associe a <span className="text-white font-medium">{email}</span>,
                vous recevrez un lien de reinitialisation dans quelques instants.
              </p>
              <p className="text-slate-500 text-xs">Pensez a verifier vos spams.</p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-[#0099CC] hover:text-[#00BBEE] transition-colors text-sm font-medium mt-4"
              >
                <ArrowLeft className="w-4 h-4" /> Retour a la connexion
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-bold text-white mb-1">Mot de passe oublie ?</h2>
              <p className="text-slate-400 text-sm mb-6">
                Saisissez votre adresse email et nous vous enverrons un lien pour reinitialiser votre mot de passe.
              </p>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm rounded-lg p-3 mb-4" data-testid="forgot-password-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-slate-300 block mb-1.5">Adresse email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <input
                      data-testid="forgot-password-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full bg-[#0B0F19] border border-slate-700 rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#0099CC] outline-none transition-colors"
                      placeholder="votre@email.com"
                      autoFocus
                    />
                  </div>
                </div>
                <button
                  data-testid="forgot-password-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#0099CC] to-[#0077AA] text-white py-3 rounded-lg font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer le lien"}
                </button>
              </form>

              <div className="text-center mt-6">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
                >
                  <ArrowLeft className="w-4 h-4" /> Retour a la connexion
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
